import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { INCIDENTS_LIST_QUERY_KEY } from '@/modules/common/queries/incidents.ts';
import {
  PUBLIC_REGISTERS_LIST_QUERY_KEY,
  REGISTERS_LIST_QUERY_KEY,
} from '@/modules/common/queries/registers.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CUSTOM_CHARTS_DATA_QUERY_KEY,
  CUSTOM_CHARTS_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import {
  CreateTaskResultRequest,
  GetGraphicDataV1TasksGraphicDataGetParams,
  GetMonthStatisticsV1TasksMonthStatisticsGetParams,
  GetTaskListResponse,
  GetTaskListV1TasksGetParams,
  GetTaskPublicAccessResponse,
  GetTaskPublicAccessV1TasksPublicAccessGetParams,
  GetTaskResponse,
  GetTasksByIncidentResponse,
  PublicAccessCreateTaskResultRequest,
} from 'compliance-api';

export const CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY = 'vestra-compliance/common-tasks-graphic-data';
export const CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY =
  'vestra-compliance/common-tasks-monthly-statistic';
export const CURRENT_USER_TASKS_LIST_QUERY_KEY = 'vestra-compliance/common-tasks-list';
export const CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY =
  'vestra-compliance/common-tasks-by-linked-incident-list';
export const CURRENT_USER_TASK_QUERY_KEY = 'vestra-compliance/employee-task';
export const CURRENT_USER_PUBLIC_TASK_QUERY_KEY = 'vestra-compliance/employee-public-task';

export const useTasksMonthStatisticQuery = (
  payload: GetMonthStatisticsV1TasksMonthStatisticsGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getMonthStatisticsV1TasksMonthStatisticsGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksListQuery = (payload: GetTaskListV1TasksGetParams, disabled = false) => {
  const businessId = useGetCurrentBusinessId();
  const { page, ...data } = payload;
  return useInfiniteQuery<GetTaskListResponse>({
    queryKey: [CURRENT_USER_TASKS_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60 * 5, //5min
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getTaskListV1TasksGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useTasksListByLinkedIncidentQuery = (id: number, disabled = false) => {
  return useQuery<GetTasksByIncidentResponse>({
    queryKey: [CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY, id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response =
        await api.v1.getTasksByIncidentResultV1TasksByIncidentResultIncidentResultIdGet({
          incidentResultId: id,
        });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useTasksGraphicDataQuery = (
  payload: GetGraphicDataV1TasksGraphicDataGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getGraphicDataV1TasksGraphicDataGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useGetTaskQuery = (taskId: number, disabled = false) => {
  return useQuery<GetTaskResponse>({
    queryKey: [CURRENT_USER_TASK_QUERY_KEY, taskId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTaskV1TasksIdGet({ id: taskId });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useGetPublicTaskQuery = (
  data: GetTaskPublicAccessV1TasksPublicAccessGetParams,
  disabled = false,
) => {
  return useQuery<GetTaskPublicAccessResponse>({
    queryKey: [CURRENT_USER_PUBLIC_TASK_QUERY_KEY, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getTaskPublicAccessV1TasksPublicAccessGet(data);
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useResolveTaskMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateTaskResultRequest) => {
      const response = await withErrorHandling(api.v1.createTaskResultV1TasksResultPost(payload), {
        loading: 'Submitting...',
        notificationDuration: 10000,
        success: 'Task completed and results submitted, thank you!',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([PUBLIC_REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_DATA_QUERY_KEY]);
    },
  });
};

export const usePublicResolveTaskMutation = () => {
  return useMutation({
    mutationFn: async (payload: PublicAccessCreateTaskResultRequest) => {
      const response = await withErrorHandling(
        api.v1.publicAccessCreateTaskResultV1TasksPublicAccessResultPost(payload),
        {
          loading: 'Submitting...',
          notificationDuration: 10000,
          success: 'Task completed and results submitted, thank you!',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY]);
      queryClient.invalidateQueries([REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([PUBLIC_REGISTERS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY]);
    },
  });
};
